import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { arboristProfileService } from '../../../features/arborist-profile/services/arboristProfileService'
import type {
  ArboristName,
  ArboristProfile,
  ArboristProfilePayload,
} from '../../../features/arborist-profile/services/arboristProfileService'
import { supabase } from '../../../services/supabase/client'
import { logout } from './authSlice'

interface ArboristProfileState {
  profile: ArboristProfile | null
  /** The user's own name (users row, not arborist_profiles) */
  name: ArboristName | null
  /** True once a fetch has completed — distinguishes "no profile" from "not fetched yet" */
  loaded: boolean
  loading: boolean
  saving: boolean
  error: string | null
  saveError: string | null
}

const initialState: ArboristProfileState = {
  profile: null,
  name: null,
  loaded: false,
  loading: false,
  saving: false,
  error: null,
  saveError: null,
}

export const fetchArboristProfile = createAsyncThunk('arboristProfile/fetch', async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')
  // The name is best-effort: a failed fetch must not fail the profile, or
  // callers that refetch while !loaded would retry forever
  const [profile, name] = await Promise.all([
    arboristProfileService.fetchProfile(user.id),
    arboristProfileService.fetchName(user.id).catch(() => null),
  ])
  return { profile, name }
})

export const saveArboristProfile = createAsyncThunk(
  'arboristProfile/save',
  async (payload: ArboristProfilePayload) => {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')
    return await arboristProfileService.upsertProfile(user.id, payload)
  }
)

export const saveArboristName = createAsyncThunk(
  'arboristProfile/saveName',
  async (name: ArboristName) => await arboristProfileService.updateName(name)
)

const arboristProfileSlice = createSlice({
  name: 'arboristProfile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchArboristProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchArboristProfile.fulfilled, (state, action) => {
        state.loading = false
        state.loaded = true
        state.profile = action.payload.profile
        state.name = action.payload.name
      })
      .addCase(fetchArboristProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to load profile'
      })
      .addCase(saveArboristProfile.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(saveArboristProfile.fulfilled, (state, action) => {
        state.saving = false
        state.profile = action.payload
      })
      .addCase(saveArboristProfile.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.error.message ?? 'Failed to save profile'
      })
      .addCase(saveArboristName.pending, (state) => {
        state.saving = true
        state.saveError = null
      })
      .addCase(saveArboristName.fulfilled, (state, action) => {
        state.saving = false
        state.name = action.payload
      })
      .addCase(saveArboristName.rejected, (state, action) => {
        state.saving = false
        state.saveError = action.error.message ?? 'Failed to save your name'
      })
      .addCase(logout.fulfilled, (state) => {
        Object.assign(state, initialState)
      })
  },
})

export const arboristProfileReducer = arboristProfileSlice.reducer
